import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Mail, ChevronRight, X, Loader2, CheckCircle2, AlertTriangle, Clock, User as UserIcon } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

/**
 * TodaysFollowupsPopup — Mounts inside the Shell. On every login (and
 * whenever the browser regains focus) fetches ``GET /api/followups/todays-alerts``
 * and, if there are pending follow-ups for TODAY or OVERDUE, opens a
 * dialog listing them one-by-one so the operator can dispatch them
 * without navigating.
 *
 * Dismiss rules
 * -------------
 *   - "Adiar 1h" — closes the popup for one hour (localStorage snooze).
 *   - "Ver na página" — closes and navigates to /followups.
 *   - After a follow-up is completed the popup refreshes and advances
 *     to the next item. When list becomes empty the popup auto-closes.
 *
 * We DO NOT show the popup more than once per session unless a NEW
 * item appears, to avoid nagging. We also skip during business hours
 * only (silent between 20:00 and 07:00 LX).
 */
const SNOOZE_KEY = "caring:followupPopupSnoozedUntil";
const SEEN_KEY = "caring:followupPopupLastSeenIds";

function isSnoozed() {
  try {
    const until = Number(localStorage.getItem(SNOOZE_KEY) || 0);
    return until && Date.now() < until;
  } catch { return false; }
}
function snoozeFor(ms) {
  try { localStorage.setItem(SNOOZE_KEY, String(Date.now() + ms)); } catch {}
}
function withinBusinessHours() {
  // Business hours are always Europe/Lisbon (operator ops window),
  // regardless of the browser timezone. Uses Intl to derive the hour
  // in Lisbon so operators travelling / DST edges see identical timing.
  try {
    const h = Number(new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/Lisbon",
      hour: "2-digit",
      hour12: false,
    }).format(new Date()));
    return h >= 7 && h < 20;
  } catch {
    const h = new Date().getHours();
    return h >= 7 && h < 20;
  }
}

export default function TodaysFollowupsPopup() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [outcome, setOutcome] = useState("done");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async ({ auto = false } = {}) => {
    try {
      const r = await api.get("/followups/todays-alerts");
      const list = r.data?.items || [];
      setItems(list);
      if (auto) {
        if (!list.length) return;
        if (isSnoozed()) return;
        if (!withinBusinessHours()) return;
        // Only auto-open if there is at least one item the user hasn't
        // dismissed already in this browser session.
        let seen = new Set();
        try { seen = new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || "[]")); } catch {}
        const hasFresh = list.some((it) => !seen.has(it.fu_id));
        if (hasFresh) {
          setIdx(0);
          setOpen(true);
        }
      }
    } catch (e) { /* silent — this is a background feature */ }
  }, []);

  // Initial + focus + periodic refresh
  useEffect(() => {
    load({ auto: true });
    const it = setInterval(() => load({ auto: true }), 5 * 60 * 1000);
    const onFocus = () => load({ auto: true });
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(it); window.removeEventListener("focus", onFocus); };
  }, [load]);

  const current = items[idx];
  const remaining = items.length - idx;

  const dismissForSession = () => {
    try {
      const ids = items.map((i) => i.fu_id);
      localStorage.setItem(SEEN_KEY, JSON.stringify(ids));
    } catch {}
    setOpen(false);
  };

  const goToFollowupsPage = () => {
    dismissForSession();
    nav("/followups");
  };

  const openLead = () => {
    if (!current) return;
    dismissForSession();
    nav(`/leads/${current.lead_id}`);
  };

  const snoozeOneHour = () => {
    snoozeFor(60 * 60 * 1000);
    setOpen(false);
    toast.info("Follow-ups adiados por 1 hora");
  };

  const complete = async () => {
    if (!current) return;
    setBusy(true);
    try {
      await api.post(
        `/leads/${current.lead_id}/scheduled-followups/${current.fu_id}/complete`,
        { outcome, note: note.trim() || undefined },
      );
      toast.success("Follow-up concluído");
      // Remove from list and advance
      const next = items.filter((_, i) => i !== idx);
      setItems(next);
      setNote("");
      setOutcome("done");
      if (idx >= next.length) setIdx(Math.max(0, next.length - 1));
      if (!next.length) setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Erro a concluir");
    } finally { setBusy(false); }
  };

  const skipToNext = () => {
    if (idx + 1 < items.length) { setIdx(idx + 1); setNote(""); setOutcome("done"); }
    else { dismissForSession(); }
  };

  if (!current) return null;

  const isOverdue = (() => {
    try {
      const d = new Date(current.scheduled_for);
      const startToday = new Date();
      startToday.setHours(0, 0, 0, 0);
      return d < startToday;
    } catch { return false; }
  })();

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? dismissForSession() : setOpen(v))}>
      <DialogContent className="max-w-lg" data-testid="todays-followups-popup">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <span className={isOverdue ? "shift-chip shift-chip-danger" : "shift-chip shift-chip-warn"}>
              {isOverdue ? "Atrasado" : "Hoje"}
            </span>
            <span className="text-xs text-muted-foreground font-medium">
              {remaining} pendente{remaining === 1 ? "" : "s"}
            </span>
          </div>
          <DialogTitle className="font-heading text-2xl font-bold tracking-tight" data-testid="popup-lead-name">
            {current.lead_name}
          </DialogTitle>
          <DialogDescription>
            Follow-up {isOverdue ? "atrasado" : "de hoje"}. Registe o resultado da chamada abaixo para concluir. Restam {remaining} pendente{remaining === 1 ? "" : "s"}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {/* Contact block */}
          <div className="grid grid-cols-2 gap-2 text-sm">
            {current.lead_phone && (
              <a href={`tel:${current.lead_phone}`}
                 className="inline-flex items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2 hover:border-primary/60 hover:bg-accent transition-colors font-medium text-foreground"
                 data-testid="popup-call-link"
                 title="Ligar agora">
                <Phone className="w-4 h-4 text-primary" />
                <span className="truncate">{current.lead_phone}</span>
              </a>
            )}
            {current.lead_email && (
              <a href={`mailto:${current.lead_email}`}
                 className="inline-flex items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2 hover:border-primary/60 hover:bg-accent transition-colors font-medium text-foreground"
                 data-testid="popup-email-link"
                 title="Enviar email">
                <Mail className="w-4 h-4 text-primary" />
                <span className="truncate text-xs">{current.lead_email}</span>
              </a>
            )}
          </div>

          {/* Metadata */}
          <div className="text-xs text-muted-foreground space-y-1">
            {current.reason && (
              <div><span className="uppercase tracking-wider text-[10px] font-semibold mr-1.5">Motivo:</span>{current.reason}</div>
            )}
            <div className="flex flex-wrap gap-3">
              <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />
                {(() => { try { return new Date(current.scheduled_for).toLocaleString("pt-PT"); } catch { return current.scheduled_for; } })()}
              </span>
              <span className="inline-flex items-center gap-1"><UserIcon className="w-3 h-3" />
                Tentativa #{current.attempt_number}
              </span>
              {current.kind === "reengagement" && (
                <span className="shift-chip shift-chip-info !text-[10px] !px-2 !py-0">Reengagement 30d</span>
              )}
            </div>
          </div>

          {/* Outcome + note (post-call) */}
          <div className="border-t border-border pt-3 space-y-2">
            <label className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Resultado da chamada</label>
            <div className="flex flex-wrap gap-1.5">
              {[
                { v: "done", l: "Falei", tone: "shift-chip-sage" },
                { v: "no_answer", l: "Não atendeu", tone: "shift-chip-warn" },
                { v: "voicemail", l: "Voicemail", tone: "shift-chip-info" },
                { v: "reschedule", l: "Reagendar", tone: "shift-chip-info" },
              ].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setOutcome(o.v)}
                  data-testid={`popup-outcome-${o.v}`}
                  className={`shift-chip ${o.tone} ${outcome === o.v ? "ring-2 ring-primary ring-offset-1 ring-offset-background" : "opacity-60 hover:opacity-100"}`}
                >
                  {o.l}
                </button>
              ))}
            </div>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Nota rápida sobre a chamada (opcional)…"
              rows={2}
              className="text-sm resize-none"
              data-testid="popup-note"
            />
          </div>
        </div>

        <DialogFooter className="flex flex-row flex-wrap items-center gap-2 justify-between">
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={snoozeOneHour} data-testid="popup-snooze">
              Adiar 1h
            </Button>
            <Button variant="ghost" size="sm" onClick={goToFollowupsPage} data-testid="popup-see-page">
              Ver na página
            </Button>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={openLead} data-testid="popup-open-lead">
              Abrir lead
            </Button>
            {items.length > 1 && (
              <Button variant="secondary" size="sm" onClick={skipToNext} data-testid="popup-next">
                Próximo <ChevronRight className="w-4 h-4" />
              </Button>
            )}
            <Button onClick={complete} disabled={busy} size="sm" data-testid="popup-complete">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4" /> Concluir</>}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
